import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Typography,
  IconButton,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  TABLE_HEAD_PORTFOLIO_LONG,
  TABLE_HEAD_PORTFOLIO_SHORT,
  applySortFilter,
  getComparator,
} from "../../../component/Methods";
import PositionTableV2 from "./PositionTableV2";
import PositionTableHeadV2 from "./PositionTableHeadV2";
import {
  getTableShellSx,
  getSideHeaderSx,
} from "./positionTableV2Styles";
import { v2CardBg, v2CardBorder, v2LabelSx } from "../../PerformanceDashboardV2/v2Theme";

const PositionPopUpV2 = ({
  title,
  open,
  setOpen,
  data,
  positionType,
  fundVal,
  strategyVal,
}) => {
  const theme = useTheme();
  const isLong = positionType === "long";
  const isDark = theme.palette.mode === "dark";

  const defaultOrderBy = isLong ? "long_size" : "short_size";
  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [order, setOrder] = useState("desc");

  const tableHead = isLong ? TABLE_HEAD_PORTFOLIO_LONG : TABLE_HEAD_PORTFOLIO_SHORT;
  const tickerColumnId = isLong ? "long_tickerSymbol" : "short_tickerSymbol";

  const filteredData = useMemo(
    () =>
      data
        ? applySortFilter(data, getComparator(order, orderBy), "")
        : [],
    [data, order, orderBy]
  );

  useEffect(() => {
    if (open) {
      setOrderBy(defaultOrderBy);
      setOrder("desc");
    }
  }, [open, defaultOrderBy]);

  const handleRequestSort = (_event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleClose = () => setOpen(false);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: isDark ? theme.palette.grey[50] : "#ebebeb",
            backgroundImage: "none",
          },
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          maxWidth: 1640,
          mx: "auto",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            ...getTableShellSx(theme),
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...getSideHeaderSx(theme, positionType),
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.9rem", mt: 0.25, ...v2LabelSx(theme) }}
              >
                {filteredData.length} positions · all columns
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              aria-label="Close fullscreen view"
              size="small"
              sx={{
                color: "text.secondary",
                border: "1px solid",
                borderColor: v2CardBorder(theme),
                borderRadius: 1.5,
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              backgroundColor: v2CardBg(theme),
            }}
          >
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              <Table size="small" stickyHeader sx={{ width: "100%" }}>
                <PositionTableHeadV2
                  columns={tableHead}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  tickerColumnId={tickerColumnId}
                />
                <TableBody>
                  {filteredData.map((row, index) => (
                    <PositionTableV2
                      key={`${row[tickerColumnId]}-${index}`}
                      row={row}
                      fundVal={fundVal}
                      strategyVal={strategyVal}
                      positionType={positionType}
                      expanded
                    />
                  ))}
                  {!filteredData.length && (
                    <TableRow>
                      <TableCell
                        colSpan={tableHead.length}
                        sx={{ border: "none", py: 6, textAlign: "center" }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No positions
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PositionPopUpV2;
